<template>
  <div class="overview-upcoming ov-card">
    <div class="ov-block-title">
      <i class="material-icons ov-title-icon">event</i>
      <h2>{{ $t('dash.overview.upcoming.title') }}</h2>
      <span v-if="!loading && items.length" class="ov-chip-muted">{{ items.length }}</span>
      <button class="ov-arrow-link ov-block-link" @click="$emit('go', { name: 'posts2', tab: 'planner' })">{{ $t('dash.overview.upcoming.planner') }}<i class="material-icons">arrow_forward</i></button>
    </div>

    <div v-if="loading" class="ov-skeleton-list">
      <div v-for="n in 4" :key="n" class="ov-skeleton-line"></div>
    </div>

    <div v-else-if="!groups.length" class="ov-empty">
      <p>{{ $t('dash.overview.upcoming.empty') }}</p>
      <button v-if="canEdit" class="ov-btn ov-btn--primary" @click="$emit('go', { name: 'postCreator' })">
        <i class="material-icons">add</i>{{ $t('dash.overview.actions.create_post') }}
      </button>
    </div>

    <div v-else class="upcoming-days">
      <div v-for="group in groups" :key="group.day" class="upcoming-day">
        <p class="upcoming-day-label">{{ group.label }}</p>
        <ul>
          <li v-for="item in group.items" :key="item.key" class="upcoming-item" :class="[item.is_draft ? 'upcoming-item--draft' : 'upcoming-item--scheduled', { 'upcoming-item--blocked': item.risk }]" @click="$emit('open-post', item)">
            <span class="upcoming-time">{{ item.time }}</span>
            <div class="upcoming-avatar"><page-image-avatar v-if="item.page" :page="item.page" /></div>
            <div class="upcoming-text">
              <div class="upcoming-head">
                <Tooltip :text="item.is_draft ? $t('dash.overview.help.upcoming.draft') : $t('dash.overview.help.upcoming.scheduled')" position="top" class="upcoming-status-wrap">
                  <span class="upcoming-status">{{ item.is_draft ? $t('dash.overview.upcoming.draft') : $t('dash.overview.upcoming.scheduled') }}</span>
                </Tooltip>
                <p>{{ item.text || $t('dash.overview.posts.no_text') }}</p>
              </div>
              <small v-if="item.page">{{ item.page.name }}</small>
              <Tooltip v-if="item.risk" :text="$t(`dash.overview.risk.${item.risk}_help`)" position="top">
                <span class="upcoming-blocked"><i class="material-icons">warning</i>{{ $t(`dash.overview.risk.${item.risk}_long`) }}</span>
              </Tooltip>
            </div>
            <img v-if="item.image" :src="item.image" class="upcoming-thumb" alt="" loading="lazy" referrerpolicy="no-referrer" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import PageImageAvatar from '../PageImageAvatar.vue'
import Tooltip from '../tooltip/tooltipInit.vue'

export default {
  name: 'OverviewUpcoming',

  components: { PageImageAvatar, Tooltip },

  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false }
  },

  computed: {
    groups() {
      const groups = {}
      this.items.forEach(item => {
        const day = moment(item.date).format('YYYY-MM-DD')
        if (!groups[day]) groups[day] = { day, label: this.dayLabel(item.date), items: [] }
        groups[day].items.push({ ...item, time: moment(item.date).format('HH:mm') })
      })
      return Object.values(groups).sort((a, b) => (a.day < b.day ? -1 : 1))
    }
  },

  methods: {
    dayLabel(date) {
      const d = moment(date)
      if (d.isSame(moment(), 'day')) return this.$t('dash.overview.upcoming.today')
      if (d.isSame(moment().add(1, 'day'), 'day')) return this.$t('dash.overview.upcoming.tomorrow')
      return d.locale((this.$i18n && this.$i18n.locale) || 'fr').format('dddd D MMMM')
    }
  }
}
</script>

<style scoped>
.upcoming-days {
  max-height: 480px;
  overflow-y: auto;
  padding-right: 4px;
}
.upcoming-day + .upcoming-day {
  margin-top: 14px;
}
.upcoming-day-label {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b1f4d;
  background: #f1edf5;
  border-radius: 20px;
  padding: 3px 10px;
  margin-bottom: 8px;
}
.upcoming-day ul {
  list-style: none;
  margin: 0 0 0 8px;
  padding: 0 0 0 12px;
  border-left: 2px solid #f1edf5;
}
.upcoming-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}
/* Point centré sur la barre : ul padding 12px + bordure 2px => axe à -13px du li */
.upcoming-item::before {
  content: '';
  position: absolute;
  box-sizing: border-box;
  left: -19px;
  top: 50%;
  width: 12px;
  height: 12px;
  margin-top: -6px;
  border-radius: 50%;
  background: #52d597;
  border: 2px solid #fff;
}
/* Couleurs du $status-styles de Posts2 : scheduled vert, draft gris */
.upcoming-item--draft::before {
  background: #848d9f;
}
.upcoming-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.upcoming-status-wrap {
  flex-shrink: 0;
}
.upcoming-status {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-radius: 20px;
  padding: 1px 7px;
  background: #e1f2e9;
  color: #2f9e6a;
}
.upcoming-item--draft .upcoming-status {
  background: #dce2ed;
  color: #5f6878;
}
/* Page sans connexion utilisable : la publication ne partira pas */
.upcoming-item--blocked {
  background: #fdf2f2;
}
.upcoming-item--blocked::before {
  background: #ff6969;
}
.upcoming-blocked {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  font-size: 11.5px;
  font-weight: 600;
  color: #b42318;
}
.upcoming-blocked .material-icons {
  font-size: 14px;
  color: #e04848;
}
.upcoming-text small {
  display: block;
}
.upcoming-item:hover {
  background: #f6f1fa;
}
.upcoming-time {
  font-size: 12px;
  font-weight: 700;
  color: #6b1f4d;
  min-width: 40px;
}
.upcoming-avatar {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 36px;
}
.upcoming-text {
  flex: 1;
  min-width: 0;
}
.upcoming-text p {
  min-width: 0;
  font-size: 13px;
  color: #121826;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}
.upcoming-text small {
  font-size: 11px;
  color: #9ba3b7;
}
.upcoming-thumb {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}
</style>
